/**
 * SMSWiser country creative mutations
 * For Media Devoted | Created By Ciro Autiero
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';

import api from '@/services/api';

import { keys } from '../keys';
import type { CampaignCreative, CampaignCreativeUpdate } from '../types';
import { unwrapApiData } from '../utils';

export function useUpdateCountryTitlePrefix(countryKey: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (titlePrefix: string | null) => {
      const res = await api.patch(
        `/smswiser/countries/${encodeURIComponent(countryKey)}/title-prefix`,
        { titlePrefix: titlePrefix ?? null },
      );
      return unwrapApiData<CampaignCreative & { titlePrefix?: string | null }>(res.data);
    },
    onSuccess: (data) => {
      qc.setQueryData(keys.countryCreative(countryKey), data);
    },
  });
}

/** @deprecated Use useUpdateCountryTitlePrefix - title prefix is now country-level */
export function useUpdateCampaignTitlePrefix(countryKey: string, _wiserId: number) {
  return useUpdateCountryTitlePrefix(countryKey);
}

/** @deprecated Use useUpdateCountryCreative */
export function useUpdateCampaignCreative(countryKey: string, _wiserId: number) {
  return useUpdateCountryCreative(countryKey);
}

export function useUpdateCountryCreative(countryKey: string) {
  const qc = useQueryClient();
  const creativeKey = keys.countryCreative(countryKey);
  return useMutation({
    mutationFn: async (payload: CampaignCreativeUpdate) => {
      const res = await api.put(
        `/smswiser/countries/${encodeURIComponent(countryKey)}/creative`,
        payload,
      );
      return unwrapApiData<CampaignCreative>(res.data);
    },
    onSuccess: (data) => {
      qc.setQueryData(creativeKey, data);
    },
  });
}

/** @deprecated Use useDeleteCountrySender */
export function useDeleteCampaignSender(countryKey: string, _wiserId: number) {
  return useDeleteCountrySender(countryKey);
}

export function useDeleteCountrySender(countryKey: string) {
  const qc = useQueryClient();
  const creativeKey = keys.countryCreative(countryKey);
  return useMutation({
    mutationFn: async (senderName: string) => {
      const res = await api.delete(
        `/smswiser/countries/${encodeURIComponent(countryKey)}/creative/senders`,
        { data: { senderName } },
      );
      return unwrapApiData<CampaignCreative>(res.data);
    },
    onSuccess: (data) => {
      qc.setQueryData(creativeKey, data);
    },
  });
}

export function useDeleteCountrySendersBulk(countryKey: string) {
  const qc = useQueryClient();
  const creativeKey = keys.countryCreative(countryKey);
  return useMutation({
    mutationFn: async (senderNames: string[]) => {
      const res = await api.delete(
        `/smswiser/countries/${encodeURIComponent(countryKey)}/creative/senders/bulk`,
        { data: { senderNames } },
      );
      return unwrapApiData<CampaignCreative>(res.data);
    },
    onSuccess: (data) => {
      qc.setQueryData(creativeKey, data);
    },
  });
}

/** @deprecated Use useDeleteCountryText */
export function useDeleteCampaignText(countryKey: string, _wiserId: number) {
  return useDeleteCountryText(countryKey);
}

export function useDeleteCountryText(countryKey: string) {
  const qc = useQueryClient();
  const creativeKey = keys.countryCreative(countryKey);
  return useMutation({
    mutationFn: async (smsText: string) => {
      const res = await api.delete(
        `/smswiser/countries/${encodeURIComponent(countryKey)}/creative/texts`,
        { data: { smsText } },
      );
      return unwrapApiData<CampaignCreative>(res.data);
    },
    onSuccess: (data) => {
      qc.setQueryData(creativeKey, data);
    },
  });
}

export function useDeleteCountryTextsBulk(countryKey: string) {
  const qc = useQueryClient();
  const creativeKey = keys.countryCreative(countryKey);
  return useMutation({
    mutationFn: async (smsTexts: string[]) => {
      const res = await api.delete(
        `/smswiser/countries/${encodeURIComponent(countryKey)}/creative/texts/bulk`,
        { data: { smsTexts } },
      );
      return unwrapApiData<CampaignCreative>(res.data);
    },
    onSuccess: (data) => {
      qc.setQueryData(creativeKey, data);
    },
  });
}
